// The browser's own speech recognition, beside the agent: live words during
// a spoken conversation, and dictation into the Prime composer. Chrome and
// Safari carry it (Safari asks for it separately from the microphone, and
// only behind a gesture); elsewhere `voiceAvailable` is false and the callers
// hide what needs it.

import { useRef, useState } from 'react'

type Recognition = {
  lang: string
  interimResults: boolean
  continuous: boolean
  maxAlternatives: number
  onresult:
    ((e: { results: ArrayLike<ArrayLike<{ transcript: string }> & { isFinal: boolean }> }) => void) | null
  onerror: ((e: { error: string }) => void) | null
  onend: (() => void) | null
  start: () => void
  stop: () => void
  abort: () => void
}

const recognitionCtor = (): (new () => Recognition) | null => {
  const w = window as unknown as {
    SpeechRecognition?: new () => Recognition
    webkitSpeechRecognition?: new () => Recognition
  }
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null
}

export const voiceAvailable = () => recognitionCtor() !== null

/**
 * Dictation for one button: `start` listens until the speaker stops and hands
 * the sentence to `onFinal`; `heard` is the interim text for the field to show
 * as it comes.
 */
export const useDictation = (onFinal: (text: string) => void) => {
  const [listening, setListening] = useState(false)
  const [heard, setHeard] = useState('')
  const [problem, setProblem] = useState<string | null>(null)
  const stop = useRef<() => void>(() => {})
  const start = () => {
    if (listening) {
      stop.current()
      setListening(false)
      setHeard('')
      return
    }
    setProblem(null)
    setHeard('')
    setListening(true)
    stop.current = listen(
      (t) => setHeard(t),
      (t) => {
        setListening(false)
        setHeard('')
        onFinal(t)
      },
      (msg) => {
        setListening(false)
        setHeard('')
        setProblem(msg)
      },
    )
  }
  return { listening, heard, problem, start }
}

/**
 * Listen continuously and report the words as they come, restarting when the
 * recogniser stops on its own (it does, every so often). For display only:
 * nothing is done with the text. Start it inside a tap: iOS asks for speech
 * recognition separately from the microphone, and only with a gesture behind
 * it. Returns a stop.
 */
export const listenInterim = (
  onText: (text: string) => void,
  onStart?: () => void,
  onError?: (why: string) => void,
): (() => void) => {
  const Ctor = recognitionCtor()
  if (!Ctor) return () => {}
  let stopped = false
  let r: Recognition | null = null
  const begin = () => {
    if (stopped) return
    const rec = new Ctor()
    r = rec
    rec.lang = navigator.language || 'en-GB'
    rec.interimResults = true
    rec.continuous = true
    rec.maxAlternatives = 1
    rec.onresult = (e) => {
      // only what is still being said: a continuous recogniser keeps every
      // finished sentence of the call, and the platform shows those itself
      let text = ''
      for (let i = e.results.length - 1; i >= 0 && !e.results[i]!.isFinal; i--) {
        text = (e.results[i]![0]?.transcript ?? '') + text
      }
      onText(text.trim())
    }
    rec.onerror = (e) => {
      onError?.(e.error)
      // a refusal will not fix itself; a hiccup will
      if (e.error === 'not-allowed' || e.error === 'service-not-allowed') stopped = true
    }
    rec.onend = () => {
      onText('')
      if (!stopped) window.setTimeout(begin, 150)
    }
    try {
      rec.start()
      onStart?.()
    } catch (e) {
      onError?.(e instanceof Error ? e.message : String(e))
    }
  }
  begin()
  return () => {
    stopped = true
    try {
      r?.abort()
    } catch {
      /* fine */
    }
  }
}

/** Listen once: interim text as it comes, the final text when the speaker stops. */
const listen = (
  onInterim: (text: string) => void,
  onFinal: (text: string) => void,
  onError: (msg: string) => void,
): (() => void) => {
  const Ctor = recognitionCtor()
  if (!Ctor) {
    onError('This browser has no speech recognition.')
    return () => {}
  }
  const r = new Ctor()
  r.lang = navigator.language || 'en-GB'
  r.interimResults = true
  r.continuous = false
  r.maxAlternatives = 1
  let finalText = ''
  r.onresult = (e) => {
    let interim = ''
    for (let i = 0; i < e.results.length; i++) {
      const res = e.results[i]!
      const t = res[0]?.transcript ?? ''
      if (res.isFinal) finalText += t
      else interim += t
    }
    onInterim((finalText + ' ' + interim).trim())
  }
  r.onerror = (e) =>
    onError(
      e.error === 'not-allowed'
        ? 'The microphone is blocked for this site.'
        : e.error === 'no-speech'
          ? 'Nothing heard.'
          : `Speech: ${e.error}`,
    )
  r.onend = () => {
    if (finalText.trim()) onFinal(finalText.trim())
  }
  r.start()
  return () => r.stop()
}
